import { DownloadOutlined, RedoOutlined } from '@ant-design/icons';
import { downloadExportLog, getExportLog } from 'api';
import { LineVertical, Pagination } from 'components';
import { ButtonTextIcon } from 'components/Button';
import Breadcrumb from 'components/Layout/ApplicationLayout/Breadcrumb';
import LayoutDefault from 'components/Layout/LayoutDefault';
import { DATE_FORMAT_LIST } from 'constant/notificationConstants';
import {
  defaultPageSizeOptions,
  defaultSelectPageSizeOption,
} from 'constant/Pagination/pageSizeOption';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import useErrorNetworkMessage from 'hook/useErrorNetworkMessage';
import useCustomMutate from 'hook/useQuery/useCustomMutate';
import useCustomQuery from 'hook/useQuery/useCustomQuery';
import useSetTitlePage from 'hook/useSetTitlePage';
import { IconWrapper } from 'page/Application/ManagerAccountPartner/AccountPartnerList/styled';
import FormDatePicker from 'page/Application/VisitedPage/component/DatePicker';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ButtonOkStyle } from '../ManageCompanyList/component/Form/styled';
import { HeaderListDataLayout, TableData } from './components';
import useMultiLanguageExportLog from './hook/useMultiLanguageExportLog';

dayjs.extend(isSameOrAfter);

const ExportLog = () => {
  const { useQueryAPI } = useCustomQuery();
  const { useMutationAPI } = useCustomMutate();
  const { isOnline, errorNetWork, contextErrorNetWork } =
    useErrorNetworkMessage();

  const defaultDateFrom = dayjs().subtract(30, 'day');
  const defaultDateTo = dayjs();

  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [dateRange, setDateRange] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
  });
  const [dateRangeSearch, setDateRangeSearch] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
  });
  const [countToReset, setCountToReset] = useState(0);
  const [emptyText, setEmptyText] = useState('データがありません');

  const { EXPORT_LOG_LIST_TITLE } = useMultiLanguageExportLog();

  const { data: exportLogList = [], isLoading } = useQueryAPI({
    api: getExportLog,
    key: ['getExportLogList', countToReset],
    params: {
      pageNumber,
      pageSize,
      dateFrom: dateRangeSearch.dateFrom.format('YYYY-MM-DD'),
      dateTo: dateRangeSearch.dateTo.format('YYYY-MM-DD'),
    },
  });

  const { mutate: mutateDownloadCSV, isLoading: loadingDownloadCSV } =
    useMutationAPI({
      api: downloadExportLog,
      refetchKey: 'downloadCSV',
      messageSuccess: 'ファイルを出力しました。',
      downloadFile: true,
      fileName: `${dateRangeSearch.dateFrom.format('YYYY年MM月DD日')}～${dateRangeSearch.dateTo.format('YYYY年MM月DD日')}_ログ出力`,
    });

  const listItem = useMemo(() => {
    return [
      {
        title: <Link to={'/'}>企業管理</Link>,
      },
      {
        title: EXPORT_LOG_LIST_TITLE,
      },
    ];
  }, []);

  const handleChangePageNumber = (pageNumber, pageSize) => {
    if (isOnline) {
      setPageNumber(pageNumber);
      setPageSize(pageSize);
    } else {
      errorNetWork();
    }
  };

  const handleChangePageSize = (value) => {
    if (isOnline) {
      setPageSize(value);
      setPageNumber(1);
    } else {
      errorNetWork();
    }
  };

  const handleExportCSV = () => {
    if (isOnline) {
      const payload = {
        pageNumber: 1,
        pageSize: 1,
        dateFrom: dateRangeSearch.dateFrom.format('YYYY-MM-DD'),
        dateTo: dateRangeSearch.dateTo.format('YYYY-MM-DD'),
      };
      mutateDownloadCSV(payload);
    } else {
      errorNetWork();
    }
  };

  const handleChangeDateRange = (values) => {
    if (isOnline) {
      if (values && values.length === 2) {
        const dateFrom = values[0] ? values[0] : '';
        const dateTo = values[1] ? values[1] : '';

        if (dayjs(dateFrom).isAfter(dayjs(dateTo))) {
          setDateRange({ dateFrom: dateTo, dateTo: dateFrom });
        } else {
          setDateRange({ dateFrom, dateTo });
        }
      } else {
        setDateRange({ dateFrom: defaultDateFrom, dateTo: defaultDateTo });
      }
    } else {
      errorNetWork();
    }
  };

  const handleFilterData = () => {
    if (isOnline) {
      setDateRangeSearch(dateRange);
      setCountToReset((prev) => prev + 1);
      setPageNumber(1);
      setEmptyText('データが見つかりませんでした。');
    } else {
      errorNetWork();
    }
  };

  const handleResetData = () => {
    if (isOnline) {
      setPageNumber(1);
      setPageSize(10);
      setDateRange({ dateFrom: defaultDateFrom, dateTo: defaultDateTo });
      setDateRangeSearch({ dateFrom: defaultDateFrom, dateTo: defaultDateTo });
      setEmptyText('データがありません');
      setCountToReset((prev) => prev + 1);
    } else {
      errorNetWork();
    }
  };

  useSetTitlePage(`${EXPORT_LOG_LIST_TITLE} | Soliton パートナーサイト`, true);

  return (
    <>
      <Breadcrumb listItem={listItem} titlePage={EXPORT_LOG_LIST_TITLE} />
      <LayoutDefault>
        {contextErrorNetWork}
        <HeaderListDataLayout
          disabled={isLoading}
          value={pageSize}
          handleChangePageSize={handleChangePageSize}
          pageSizeOptions={defaultSelectPageSizeOption}
          titleScreen={'ログ出力'}
        >
          <>
            <ButtonTextIcon
              text={'リフレッシュ'}
              icon={<RedoOutlined />}
              onClick={handleResetData}
              disabled={isLoading}
            />
            <LineVertical />
            <ButtonTextIcon
              disabled={loadingDownloadCSV || isLoading}
              text={'出力'}
              size={'middle'}
              icon={
                <IconWrapper
                  style={{
                    opacity: loadingDownloadCSV ? 0.3 : 1,
                  }}
                >
                  <DownloadOutlined style={{ color: '#9d1c49' }} />
                </IconWrapper>
              }
              onClick={handleExportCSV}
            />
            <LineVertical />
            <FormDatePicker
              width={'360px'}
              height={'36px'}
              dateFormat={DATE_FORMAT_LIST[1]}
              isShowTime={false}
              required={false}
              defaultFromDate={dateRange.dateFrom}
              defaultToDate={dateRange.dateTo}
              resetTrigger={countToReset}
              setDateRange={setDateRange}
              listDisable={isLoading}
            />
            <LineVertical />
            <ButtonOkStyle
              type="primary"
              htmlType="submit"
              onClick={handleFilterData}
              style={{ marginRight: 20 }}
              disabled={isLoading}
            >
              {' '}
              検索
            </ButtonOkStyle>
          </>
        </HeaderListDataLayout>

        <TableData
          dataRaw={exportLogList?.data}
          isLoading={isLoading}
          emptyText={isLoading ? '集計中です。' : emptyText}
        />

        <Pagination
          currentPage={pageNumber}
          handlePagingChange={handleChangePageNumber}
          total={exportLogList.data?.total}
          pageSize={pageSize}
          pageSizeOptions={defaultPageSizeOptions}
          handleChangePageSize={handleChangePageSize}
          totalTitle="ログ総数"
        />
      </LayoutDefault>
    </>
  );
};

export default ExportLog;
